<template>
  <div class="ic-comments">
    <div v-if="loading" class="ic-comments__loading">
      <v-progress-circular indeterminate color="primary" size="28" width="3" />
    </div>
    <div v-else-if="loadError" class="ic-comments__empty ic-comments__empty--error">
      <v-icon small>mdi-alert-circle-outline</v-icon>{{ loadError }}
      <v-btn text small color="primary" @click="load">Reîncearcă</v-btn>
    </div>
    <template v-else>
      <div v-if="!comments.length" class="ic-comments__empty">
        <v-icon small>mdi-comment-outline</v-icon>
        Niciun comentariu încă. Scrieți aici ce ați observat la acest echipament.
      </div>
      <ol v-else class="ic-comments__list">
        <li
          v-for="c in comments"
          :key="c.id"
          class="ic-comment"
          :class="{ 'ic-comment--internal': c.is_internal, 'ic-comment--mine': c.mine }"
          :data-comment-id="c.id"
        >
          <span class="ic-comment__avatar" aria-hidden="true">{{ initials(c.user) }}</span>
          <div class="ic-comment__body">
            <div class="ic-comment__head">
              <span class="ic-comment__author">{{ c.user ? c.user.name : "—" }}</span>
              <span class="ic-comment__date">{{ formatDate(c.created_at) }}</span>
              <span v-if="c.is_internal" class="ic-comment__internal" title="Îl văd doar DTI și persoana responsabilă">
                <v-icon x-small>mdi-lock-outline</v-icon>intern
              </span>
            </div>
            <p v-if="c.text" class="ic-comment__text">{{ c.text }}</p>
            <PhotoGallery
              v-if="c.files && c.files.length"
              :files="c.files"
              :size="76"
              :download-name="`comentariu-${c.id}`"
              class="ic-comment__photos"
            />
          </div>
        </li>
      </ol>
    </template>

    <form class="ic-composer" @submit.prevent="send">
      <v-textarea
        v-model="text"
        label="Comentariu nou"
        placeholder="De exemplu: tonerul se termină, cablul e deteriorat, echipamentul a fost mutat"
        outlined
        auto-grow
        rows="2"
        counter="2000"
        maxlength="2000"
        prepend-inner-icon="mdi-comment-text-outline"
        :disabled="sending"
        :error-messages="sendError"
      />
      <PhotoInput
        :key="composerKey"
        v-model="photos"
        purpose="comment"
        :max="6"
        :disabled="sending"
        label="Fotografii (opțional)"
        @uploading="uploading = $event"
        @error="sendError = $event"
      />
      <div class="ic-composer__foot">
        <v-switch
          v-if="canInternal"
          v-model="internal"
          inset
          hide-details
          color="#a8690f"
          class="ic-composer__switch mt-0"
          :disabled="sending"
        >
          <template #label>
            <span class="ic-composer__switch-label">
              <v-icon small>mdi-lock-outline</v-icon>Intern: doar DTI și persoana responsabilă
            </span>
          </template>
        </v-switch>
        <v-spacer />
        <v-btn class="btn-green" large type="submit" :loading="sending" :disabled="!canSend">
          <v-icon left>mdi-send-outline</v-icon>Trimite
        </v-btn>
      </div>
    </form>
  </div>
</template>

<script>
// The comment thread of an item (every row of its transfer lineage) + the composer.
// Internal comments: written by staff, read by staff and the item's responsible person (server rule).
import * as api from "./api";
import PhotoInput from "@/components/files/PhotoInput.vue";
import PhotoGallery from "@/components/files/PhotoGallery.vue";

export default {
  name: "ItemComments",
  components: { PhotoInput, PhotoGallery },
  props: {
    // any row id of the item (the server follows it to the current one)
    equipmentId: { type: Number, required: true },
  },
  data() {
    return {
      loading: true,
      loadError: "",
      comments: [],
      canInternal: false,
      text: "",
      photos: [],
      internal: false,
      uploading: false,
      sending: false,
      sendError: "",
      composerKey: 1,
    };
  },
  computed: {
    canSend() {
      return !this.uploading && !this.sending && (this.text.trim().length > 0 || this.photos.length > 0);
    },
  },
  watch: {
    equipmentId() {
      this.load();
    },
    text() {
      this.sendError = "";
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      this.loadError = "";
      const id = this.equipmentId;
      try {
        const data = await api.getItemComments(id);
        if (id !== this.equipmentId) return;
        this.comments = data.comments || [];
        this.canInternal = !!data.canInternal;
        this.$emit("count", this.comments.length);
      } catch (error) {
        const message = error.response && error.response.data && error.response.data.message;
        this.loadError = (typeof message === "string" && message) || "Comentariile nu au putut fi încărcate.";
      } finally {
        this.loading = false;
      }
    },
    async send() {
      if (!this.canSend) return;
      this.sending = true;
      this.sendError = "";
      try {
        const saved = await api.addItemComment(this.equipmentId, {
          text: this.text.trim(),
          is_internal: this.canInternal && this.internal,
          file_ids: this.photos.map((p) => p.id),
        });
        this.comments = [...this.comments, saved];
        this.$emit("count", this.comments.length);
        this.text = "";
        this.photos = [];
        this.internal = false;
        // a fresh PhotoInput: the sent photos are not "replaced" or forgotten from the cache
        this.composerKey += 1;
        this.$emit("posted", saved);
      } catch (error) {
        const message = error.response && error.response.data && error.response.data.message;
        this.sendError = (Array.isArray(message) ? message.join(". ") : message) || "Comentariul nu a fost trimis. Încercați din nou.";
      } finally {
        this.sending = false;
      }
    },
    initials(user) {
      const name = (user && user.name) || "";
      const parts = name.split(/\s+/).filter(Boolean);
      return ((parts[0] || "?")[0] + (parts[1] ? parts[1][0] : "")).toUpperCase();
    },
    formatDate(value) {
      if (!value) return "";
      const date = new Date(String(value).replace(" ", "T"));
      if (isNaN(date)) return String(value);
      const pad = (n) => String(n).padStart(2, "0");
      return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
    },
  },
};
</script>

<style scoped>
.ic-comments__loading {
  display: flex;
  justify-content: center;
  padding: 20px 0;
}
.ic-comments__empty {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  padding: 14px;
  margin-bottom: 16px;
  border-radius: 10px;
  background: var(--mint-50);
  color: var(--ink-3);
}
.ic-comments__empty .v-icon {
  color: var(--green) !important;
}
.ic-comments__empty--error {
  background: var(--brick-bg);
  color: var(--brick);
}
.ic-comments__empty--error .v-icon {
  color: var(--brick) !important;
}
.ic-comments__list {
  list-style: none;
  padding: 0;
  margin: 0 0 18px;
}
.ic-comment {
  display: flex;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid var(--line-soft);
}
.ic-comment:first-child {
  padding-top: 0;
}
.ic-comment__avatar {
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--pine-900);
  color: var(--leaf);
  font-weight: 700;
  font-size: 0.85rem;
}
.ic-comment--mine .ic-comment__avatar {
  background: var(--green);
  color: #fff;
}
.ic-comment--internal .ic-comment__avatar {
  background: var(--amber-bg);
  color: var(--amber);
}
.ic-comment__body {
  flex: 1;
  min-width: 0;
}
.ic-comment__head {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  gap: 4px 10px;
}
.ic-comment__author {
  font-weight: 700;
  color: var(--ink);
}
.ic-comment__date {
  color: var(--ink-3);
  font-size: 0.88rem;
}
.ic-comment__internal {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 1px 8px;
  border-radius: 999px;
  background: var(--amber-bg);
  color: var(--amber);
  font-size: 0.78rem;
  font-weight: 700;
}
.ic-comment__internal .v-icon {
  color: var(--amber) !important;
}
.ic-comment__text {
  margin: 4px 0 0;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  color: var(--ink);
  line-height: 1.45;
}
.ic-comment__photos {
  margin-top: 8px;
}
.ic-composer {
  padding-top: 4px;
}
.ic-composer__foot {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  margin-top: 14px;
}
.ic-composer__switch-label {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: var(--ink-2);
  font-weight: 600;
}
.ic-composer__switch-label .v-icon {
  color: var(--amber) !important;
}
</style>
